import {
  buildPmr171Frame,
  PMR171_COMMAND,
  type ParsedPmr171Frame,
} from '../../../services/radioSerialProtocol';

export const CHANNEL_INDEX_MIN = 0;
export const CHANNEL_INDEX_MAX = 999;
export const CHANNEL_FREQUENCY_MAX = 2_000_000_000;
export const CHANNEL_NAME_MAX_BYTES = 12;
export const DMR_CALL_ID_MIN = 1;
export const DMR_CALL_ID_MAX = 16_777_214;

/** 协议 §51 模式：0 USB … 10 DFM（含 PKT，不同于遥控页 FT8） */
export const CHANNEL_VFO_MODES = [
  'USB',
  'LSB',
  'CWR',
  'CWL',
  'AM',
  'WFM',
  'NFM',
  'DIGI',
  'PKT',
  'DMR',
  'DFM',
] as const;

export type ChannelVfoMode = (typeof CHANNEL_VFO_MODES)[number];

/**
 * 协议亚音表：索引 0 = 关，其后为标准 50 个 CTCSS。
 * DMR 的 Rx/Tx CTCSS 为 1–51，即同一张表的 1-based 下标。
 */
export const CHANNEL_CTCSS_HZ = [
  0, 67.0, 69.3, 71.9, 74.4, 77.0, 79.7, 82.5, 85.4, 88.5, 91.5, 94.8, 97.4, 100.0, 103.5,
  107.2, 110.9, 114.8, 118.8, 123.0, 127.3, 131.8, 136.5, 141.3, 146.2, 151.4, 156.7, 159.8,
  162.2, 165.5, 167.9, 171.3, 173.8, 177.3, 179.9, 183.5, 186.2, 189.9, 192.8, 196.6, 199.5,
  203.5, 206.5, 210.7, 218.1, 225.7, 229.1, 233.6, 241.8, 250.3, 254.1,
] as const;

export const ANALOG_CTCSS_MAX = CHANNEL_CTCSS_HZ.length - 1;
export const DMR_CTCSS_MIN = 1;
export const DMR_CTCSS_MAX = CHANNEL_CTCSS_HZ.length;

export interface AnalogChannelParams {
  channel: number;
  vfoAMode: number;
  vfoBMode: number;
  vfoAFrequency: number;
  vfoBFrequency: number;
  txCtcss: number;
  rxCtcss: number;
  name: string;
}

export interface DmrChannelParams {
  channel: number;
  callFormat: number;
  txCc: number;
  rxCc: number;
  slot: number;
  callId: number;
  ownId: number;
  chType: number;
  rxCtcss: number;
  txCtcss: number;
  sqlevel: number;
  spkgain: number;
  dmrexist: number;
  dmodGain: number;
  scrEn: number;
  scrSeed: number;
  chBsMode: number;
  validat: number;
}

const ANALOG_REPLY_COMMANDS = [PMR171_COMMAND.CHANNEL_WRITE, PMR171_COMMAND.CHANNEL_READ] as const;
const DMR_REPLY_COMMANDS = [PMR171_COMMAND.DMR_CHANNEL_WRITE, PMR171_COMMAND.DMR_CHANNEL_READ] as const;
const ANALOG_REPLY_MIN = 14;
const DMR_REPLY_MIN = 26;

function writeBe32(value: number): number[] {
  const hz = Math.round(value) >>> 0;
  return [(hz >> 24) & 0xff, (hz >> 16) & 0xff, (hz >> 8) & 0xff, hz & 0xff];
}

function readBe32(bytes: Uint8Array, offset: number): number {
  return (
    (((bytes[offset] & 0xff) << 24) |
      ((bytes[offset + 1] & 0xff) << 16) |
      ((bytes[offset + 2] & 0xff) << 8) |
      (bytes[offset + 3] & 0xff)) >>>
    0
  );
}

function writeBe16(value: number): number[] {
  const raw = Math.round(value) & 0xffff;
  return [(raw >> 8) & 0xff, raw & 0xff];
}

function readBe16(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] & 0xff) << 8) | (bytes[offset + 1] & 0xff);
}

function requireInt(value: number, min: number, max: number, label: string): number {
  if (!Number.isInteger(value) || value < min || value > max) {
    throw new Error(`${label}范围为 ${min}–${max}`);
  }
  return value;
}

function requireFrequency(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0 || value > CHANNEL_FREQUENCY_MAX) {
    throw new Error(`${label}范围为 0–${CHANNEL_FREQUENCY_MAX}`);
  }
  return Math.round(value);
}

export function encodeChannelIndex(channel: number): number[] {
  const index = requireInt(channel, CHANNEL_INDEX_MIN, CHANNEL_INDEX_MAX, '信道号');
  return [(index >> 8) & 0xff, index & 0xff];
}

export function analogCtcssLabel(index: number): string {
  const hz = CHANNEL_CTCSS_HZ[index];
  if (hz == null) return String(index);
  return index === 0 ? '关' : `${hz.toFixed(1)} Hz`;
}

export function dmrCtcssLabel(index: number): string {
  const hz = CHANNEL_CTCSS_HZ[index - 1];
  if (hz == null) return String(index);
  return index === 1 ? '关' : `${hz.toFixed(1)} Hz`;
}

export function parseChannelName(bytes: Uint8Array): string {
  let end = bytes.length;
  while (end > 0 && bytes[end - 1] === 0) end -= 1;
  let result = '';
  for (let i = 0; i < end && result.length < CHANNEL_NAME_MAX_BYTES; i += 1) {
    const code = bytes[i] & 0xff;
    if (code >= 0x20 && code <= 0x7e) result += String.fromCharCode(code);
  }
  return result;
}

export function encodeChannelName(name: string): number[] {
  const bytes = new Array<number>(CHANNEL_NAME_MAX_BYTES).fill(0);
  let offset = 0;
  for (const char of name) {
    if (offset >= CHANNEL_NAME_MAX_BYTES) break;
    const code = char.charCodeAt(0);
    if (code >= 0x20 && code <= 0x7e) {
      bytes[offset] = code;
      offset += 1;
    }
  }
  return bytes;
}

function payloadFrom(
  input: ParsedPmr171Frame | Uint8Array,
  commands: readonly number[],
): Uint8Array | null {
  if (!(input instanceof Uint8Array)) {
    if (!input.crcValid || !commands.includes(input.command & 0xff)) return null;
    return input.data;
  }
  if (input.length >= 8 && input[0] === 0xa5 && input[1] === 0xa5 && input[2] === 0xa5 && input[3] === 0xa5) {
    if (!commands.includes(input[5] & 0xff)) return null;
    return input.slice(6, input.length - 2);
  }
  return input;
}

export function encodeAnalogChannelWrite(params: AnalogChannelParams): Uint8Array {
  const vfoAMode = requireInt(params.vfoAMode, 0, CHANNEL_VFO_MODES.length - 1, 'VFOA 模式');
  const vfoBMode = requireInt(params.vfoBMode, 0, CHANNEL_VFO_MODES.length - 1, 'VFOB 模式');
  const txCtcss = requireInt(params.txCtcss, 0, ANALOG_CTCSS_MAX, '发射亚音');
  const rxCtcss = requireInt(params.rxCtcss, 0, ANALOG_CTCSS_MAX, '接收亚音');
  return buildPmr171Frame([
    PMR171_COMMAND.CHANNEL_WRITE,
    ...encodeChannelIndex(params.channel),
    vfoAMode,
    vfoBMode,
    ...writeBe32(requireFrequency(params.vfoAFrequency, 'VFOA 频率')),
    ...writeBe32(requireFrequency(params.vfoBFrequency, 'VFOB 频率')),
    txCtcss,
    rxCtcss,
    ...encodeChannelName(params.name),
  ]);
}

export function encodeAnalogChannelRead(channel: number): Uint8Array {
  return buildPmr171Frame([PMR171_COMMAND.CHANNEL_READ, ...encodeChannelIndex(channel)]);
}

export function encodeChannelMode(mode: number): Uint8Array {
  const value = requireInt(mode, 0, 1, '信道模式');
  return buildPmr171Frame([PMR171_COMMAND.CHANNEL_MODE, value]);
}

export function encodeDmrChannelWrite(params: DmrChannelParams): Uint8Array {
  const callFormat = requireInt(params.callFormat, 0, 2, 'Call_format');
  const txCc = requireInt(params.txCc, 0, 15, 'Tx_cc');
  const rxCc = requireInt(params.rxCc, 0, 15, 'Rx_cc');
  const slot = requireInt(params.slot, 1, 2, 'slot');
  const callId = requireInt(params.callId, DMR_CALL_ID_MIN, DMR_CALL_ID_MAX, 'Call_id');
  const ownId = requireInt(params.ownId, DMR_CALL_ID_MIN, DMR_CALL_ID_MAX, 'Own_id');
  const chType = requireInt(params.chType, 0, 1, 'Ch_type');
  const rxCtcss = requireInt(params.rxCtcss, DMR_CTCSS_MIN, DMR_CTCSS_MAX, 'Rx_ctcss');
  const txCtcss = requireInt(params.txCtcss, DMR_CTCSS_MIN, DMR_CTCSS_MAX, 'Tx_ctcss');
  const sqlevel = requireInt(params.sqlevel, 1, 5, 'sqlevel');
  const spkgain = requireInt(params.spkgain, 0, 10, 'spkgain');
  const dmrexist = requireInt(params.dmrexist, 0, 255, 'dmrexist');
  const dmodGain = requireInt(params.dmodGain, 0, 95, 'dmod_gain');
  const scrEn = requireInt(params.scrEn, 0, 1, 'scr_en');
  const scrSeed = requireInt(params.scrSeed, 0, 0xffff, 'scr_seed');
  const chBsMode = requireInt(params.chBsMode, 0, 1, 'ch_bs_mode');
  const validat = requireInt(params.validat, 0, 255, 'validat');
  return buildPmr171Frame([
    PMR171_COMMAND.DMR_CHANNEL_WRITE,
    ...encodeChannelIndex(params.channel),
    callFormat,
    txCc,
    rxCc,
    slot,
    ...writeBe32(callId),
    ...writeBe32(ownId),
    chType,
    rxCtcss,
    txCtcss,
    sqlevel,
    spkgain,
    dmrexist,
    dmodGain,
    scrEn,
    ...writeBe16(scrSeed),
    chBsMode,
    validat,
  ]);
}

export function encodeDmrChannelRead(channel: number): Uint8Array {
  return buildPmr171Frame([PMR171_COMMAND.DMR_CHANNEL_READ, ...encodeChannelIndex(channel)]);
}

export function isAnalogChannelReply(frame: ParsedPmr171Frame): boolean {
  return Boolean(parseAnalogChannelReply(frame));
}

export function parseAnalogChannelReply(
  input: ParsedPmr171Frame | Uint8Array,
): AnalogChannelParams | null {
  const data = payloadFrom(input, ANALOG_REPLY_COMMANDS);
  if (!data || data.length < ANALOG_REPLY_MIN) return null;
  const channel = readBe16(data, 0);
  const vfoAFrequency = readBe32(data, 4);
  const vfoBFrequency = readBe32(data, 8);
  if (channel > CHANNEL_INDEX_MAX) return null;
  if (vfoAFrequency > CHANNEL_FREQUENCY_MAX || vfoBFrequency > CHANNEL_FREQUENCY_MAX) return null;
  return {
    channel,
    vfoAMode: data[2] & 0xff,
    vfoBMode: data[3] & 0xff,
    vfoAFrequency,
    vfoBFrequency,
    txCtcss: data[12] & 0xff,
    rxCtcss: data[13] & 0xff,
    name: parseChannelName(data.slice(14, 14 + CHANNEL_NAME_MAX_BYTES)),
  };
}

export function parseDmrChannelReply(input: ParsedPmr171Frame | Uint8Array): DmrChannelParams | null {
  const data = payloadFrom(input, DMR_REPLY_COMMANDS);
  if (!data || data.length < DMR_REPLY_MIN) return null;
  const channel = readBe16(data, 0);
  if (channel > CHANNEL_INDEX_MAX) return null;
  return {
    channel,
    callFormat: data[2] & 0xff,
    txCc: data[3] & 0xff,
    rxCc: data[4] & 0xff,
    slot: data[5] & 0xff,
    callId: readBe32(data, 6),
    ownId: readBe32(data, 10),
    chType: data[14] & 0xff,
    rxCtcss: data[15] & 0xff,
    txCtcss: data[16] & 0xff,
    sqlevel: data[17] & 0xff,
    spkgain: data[18] & 0xff,
    dmrexist: data[19] & 0xff,
    dmodGain: data[20] & 0xff,
    scrEn: data[21] & 0xff,
    scrSeed: readBe16(data, 22),
    chBsMode: data[24] & 0xff,
    validat: data[25] & 0xff,
  };
}

export function parseChannelModeReply(input: ParsedPmr171Frame | Uint8Array): 0 | 1 | null {
  const data = payloadFrom(input, [PMR171_COMMAND.CHANNEL_MODE]);
  if (!data || data.length < 1) return null;
  const mode = data[0] & 0xff;
  return mode === 0 || mode === 1 ? mode : null;
}
